import { useEffect, useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import {
  DigitalTwin,
  SchemaCollection,
  Ruleset,
  GraphQlDmlVersion,
  Maybe,
} from 'types/graphql-apollo-v2';
import { Schema, Property } from 'hooks/projects';
import { generateSchemas as generateSchemasImpl } from 'utils/schema-parser';
import { parseRefId as parseRefIdImpl } from 'utils/misc';
import { GetDataModelVersionsByIdDocument } from 'types/graphql-apollo-v2';

export interface SchemaWithTypes {
  schemaCollection: SchemaCollection;
  types: Schema[];
  schemaFilters: Ruleset[];
  error?: {
    code: 'MISSING_SCHEMA' | 'PARSE_ERROR' | 'FETCH_ERROR' | 'UNEXPECTED_ERROR';
    message: string;
  };
}

export type UseSchemaDataDeps = {
  generateSchemas: typeof generateSchemasImpl;
  parseRefId: typeof parseRefIdImpl;
};

const defaultDeps: UseSchemaDataDeps = {
  generateSchemas: generateSchemasImpl,
  parseRefId: parseRefIdImpl,
};

/**
 * Hook to fetch all schemas with their types and schema filters for a digital twin
 */
export const useSchemaData = (
  digitalTwin: DigitalTwin | null | undefined,
  deps: UseSchemaDataDeps = defaultDeps
) => {
  const apolloClient = useApolloClient();
  const [schemas, setSchemas] = useState<SchemaWithTypes[]>([]);
  const [loading, setLoading] = useState(true);

  const schemaCollections = digitalTwin?.schemaCollections?.items || [];

  const schemaCollectionKey = useMemo(
    () =>
      schemaCollections
        .filter((sc): sc is SchemaCollection => sc != null)
        .map((sc) => `${sc.name}:${sc.refId ?? ''}`)
        .join(','),
    [schemaCollections]
  );

  useEffect(() => {
    let cancelled = false;

    if (!schemaCollectionKey) {
      setSchemas([]);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    const fetchSchemaData = async () => {
      setLoading(true);

      const results = await Promise.all(
        schemaCollections
          .filter((sc): sc is SchemaCollection => sc != null)
          .map(async (schemaCollection): Promise<SchemaWithTypes> => {
            try {
              let graphQLSchemaString = schemaCollection.graphQLSchemaString;

              if (!graphQLSchemaString && schemaCollection.refId) {
                try {
                  const parsedRef = deps.parseRefId(schemaCollection.refId);

                  if (parsedRef.space && parsedRef.dataModel) {
                    const { data: schemaData } = await apolloClient.query({
                      query: GetDataModelVersionsByIdDocument,
                      variables: {
                        space: parsedRef.space,
                        dataModel: parsedRef.dataModel,
                      },
                    });

                    const dataModelVersion =
                      schemaData?.graphQlDmlVersionsById?.items?.find(
                        (item: Maybe<GraphQlDmlVersion>) =>
                          item?.version === parsedRef.dataModelVersion
                      );

                    if (dataModelVersion?.graphQlDml) {
                      graphQLSchemaString = dataModelVersion.graphQlDml;
                    }
                  }
                } catch (fetchError: unknown) {
                  console.error(
                    '[useSchemaData] Error fetching schema from FDM:',
                    fetchError
                  );
                  const detail =
                    fetchError instanceof Error
                      ? fetchError.message
                      : String(fetchError);
                  return {
                    schemaCollection: schemaCollection as SchemaCollection,
                    types: [],
                    schemaFilters: [],
                    error: {
                      code: 'FETCH_ERROR' as const,
                      message: `Failed to fetch schema from data model: ${detail}`,
                    },
                  };
                }
              }

              let types: Schema[] = [];
              let error: SchemaWithTypes['error'] = undefined;

              if (graphQLSchemaString) {
                try {
                  types = deps.generateSchemas(
                    graphQLSchemaString,
                    schemaCollection.tenant
                  );
                } catch (parseError) {
                  console.error(
                    '[useSchemaData] Error parsing schema:',
                    parseError
                  );
                  error = {
                    code: 'PARSE_ERROR',
                    message: 'Failed to parse GraphQL schema definition',
                  };
                }
              } else {
                error = {
                  code: 'MISSING_SCHEMA',
                  message:
                    'GraphQL schema definition not available. This schema may need to be configured in the data source.',
                };
              }

              return {
                schemaCollection: schemaCollection as SchemaCollection,
                types,
                // TODO: Fetch schema filters from Galactica internal GraphQL endpoint (/api/v1/projects/graphql)
                schemaFilters: [],
                error,
              };
            } catch (error: unknown) {
              console.error(
                `[useSchemaData] Error for ${schemaCollection.name}:`,
                error
              );
              const detail =
                error instanceof Error ? error.message : String(error);
              return {
                schemaCollection: schemaCollection as SchemaCollection,
                types: [],
                schemaFilters: [],
                error: {
                  code: 'UNEXPECTED_ERROR' as const,
                  message: `Unexpected error processing schema ${schemaCollection.name}: ${detail}`,
                },
              };
            }
          })
      );

      if (!cancelled) {
        setSchemas(results);
        setLoading(false);
      }
    };

    if (schemaCollections.length > 0) {
      fetchSchemaData();
    } else {
      setSchemas([]);
      setLoading(false);
    }

    return () => {
      cancelled = true;
    };
  }, [
    schemaCollectionKey,
    apolloClient,
    deps.generateSchemas,
    deps.parseRefId,
  ]);

  return {
    schemas,
    loading,
  };
};

/**
 * Get attribute names for a schema type across all schema collections.
 * Returns properties from the first matching collection (schema types
 * are expected to be unique within a digital twin).
 */
export const getAttributesForSchemaType = (
  schemaType: string,
  schemas: SchemaWithTypes[]
): string[] => {
  for (const schema of schemas) {
    const type = schema.types.find((t) => t.name === schemaType);
    if (type) {
      return type.properties.map((prop) => prop.name);
    }
  }
  return [];
};

export type SchemaContext = {
  schemaTypes: Schema[];
  selectedType: Schema | undefined;
  typeProperties: Property[];
};

const EMPTY_SCHEMA_CONTEXT: SchemaContext = {
  schemaTypes: [],
  selectedType: undefined,
  typeProperties: [],
};

/**
 * Resolve the list of types and the specific selected type for a schema filter.
 * Used by the rule drawers to feed per-rule filter UIs and filter serializers.
 */
export const resolveSchemaContext = (
  filter: { schemaCollectionId: string; schemaType: string } | undefined,
  schemas: SchemaWithTypes[]
): SchemaContext => {
  if (!filter) return EMPTY_SCHEMA_CONTEXT;
  const schema = schemas.find(
    (s) => s.schemaCollection.externalId === filter.schemaCollectionId
  );
  if (!schema) return EMPTY_SCHEMA_CONTEXT;

  const schemaTypes = schema.types;
  const selectedType = schemaTypes.find((t) => t.name === filter.schemaType);
  return {
    schemaTypes,
    selectedType,
    typeProperties: selectedType?.properties ?? [],
  };
};
